import React from 'react';

import Button from '@atlaskit/button/standard-button';
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	type OnCloseHandler,
} from '@atlaskit/modal-dialog';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { useWhiteboardInternalModal } from './useWhiteboardInternalModal';
import type { WhiteboardInternalModalService } from './services/whiteboard-internal-modal-service';

export type WhiteboardInternalModalProps = {
	whiteboardInternalModalService: WhiteboardInternalModalService;
};

export const WhiteboardInternalModal: React.FC<WhiteboardInternalModalProps> = ({
	whiteboardInternalModalService,
}: WhiteboardInternalModalProps) => {
	const modalState = useWhiteboardInternalModal(whiteboardInternalModalService);

	if (modalState.isModalOpen) {
		const {
			props: dialogProps,
			modalHeader: {
				title: { content: titleContent, props: titleProps },
				props: headerProps,
			},
			modalBody: { content: bodyContent, props: bodyProps },
			modalFooter: { buttons, props: footerProps },
		} = modalState.modalProps;

		const onClose: OnCloseHandler = (e, analyticsEvent) => {
			dialogProps?.onClose?.(e, analyticsEvent);
			modalState.closeModal();
		};

		return (
			<ModalDialog {...dialogProps} onClose={onClose}>
				<ModalHeader {...headerProps}>
					<ModalTitle {...titleProps}>{titleContent}</ModalTitle>
				</ModalHeader>
				<ModalBody {...bodyProps}>{bodyContent}</ModalBody>
				<ModalFooter {...footerProps}>
					{buttons.map((b, i) => {
						const { shouldCloseModal, props } = b;

						const onClick = (
							e: React.MouseEvent<HTMLElement>,
							analyticsEvent: UIAnalyticsEvent,
						) => {
							props?.onClick?.(e, analyticsEvent);

							if (shouldCloseModal) {
								onClose(e, analyticsEvent);
							}
						};

						return (
							<Button {...b.props} key={`whiteboard-modal-button-${i}`} onClick={onClick}>
								{b.label}
							</Button>
						);
					})}
				</ModalFooter>
			</ModalDialog>
		);
	}

	return null;
};
